"use client";

import { AnimatePresence, motion } from "framer-motion";
import {
	ArrowRightLeft,
	Database,
	Gamepad2,
	Layers,
	TrendingUp,
	Zap,
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

interface LayerProps {
	id: string;
	title: string;
	enTitle: string;
	icon: React.ElementType;
	description: string;
	enDescription: string;
	features: string[];
	enFeatures: string[];
	color: string;
}

const layers: LayerProps[] = [
	{
		id: "data",
		title: "数据与洞察层",
		enTitle: "Data & Insights Layer",
		icon: Database,
		description:
			"看清链上正在发生什么。将原本晦涩的链上数据转化为可理解、可比较的信息结构。",
		enDescription:
			"Seeing what's happening on-chain. Transforming raw on-chain data into understandable insights.",
		features: [
			"生态与市场总览",
			"资金与行为分析",
			"盈亏与成本视角",
			"地址关联图谱",
		],
		enFeatures: [
			"Market Overview",
			"Funds & Behavior Analysis",
			"PnL & Cost Perspective",
			"Address Relationship Maps",
		],
		color: "bg-blue-500",
	},
	{
		id: "trading",
		title: "交易与执行辅助层",
		enTitle: "Trading & Execution Support",
		icon: Zap,
		description:
			"在复杂环境中减少非理性失误。不代替用户决策，而是提供核心辅助能力。",
		enDescription:
			"Reducing irrational errors. Supporting execution without replacing user judgment.",
		features: ["实时监控与关键提醒", "策略型执行辅助", "风险与异常提示"],
		enFeatures: ["Real-time Monitoring", "Strategic Execution", "Risk Alerts"],
		color: "bg-amber-500",
	},
	{
		id: "growth",
		title: "运营与增长工具层",
		enTitle: "Operations & Growth Tools",
		icon: TrendingUp,
		description: "支撑项目与生态的长期发展。提供标准化、可复用的基础设施能力。",
		enDescription:
			"Supporting long-term ecosystem growth. Providing standardized, reusable infrastructure.",
		features: ["批量空投与激励工具", "质押与参与机制", "生态协作机制"],
		enFeatures: [
			"Bulk Airdrop Tools",
			"Staking Mechanisms",
			"Ecosystem Collaboration",
		],
		color: "bg-green-500",
	},
	{
		id: "interaction",
		title: "互动与应用层",
		enTitle: "Interaction & Application Layer",
		icon: Gamepad2,
		description: "让参与成为一种长期行为。构建互动与情感连接。",
		enDescription:
			"Making participation a long-term behavior. Building interaction and emotional connection.",
		features: ["奥丁广场", "链上小游戏", "红包/任务/互动机制"],
		enFeatures: ["Odin Plaza", "On-chain Mini-games", "Red Packets/Quests"],
		color: "bg-purple-500",
	},
];

interface ArchitectureDiagramProps {
	language: "zh" | "en";
}

export function ArchitectureDiagram({ language }: ArchitectureDiagramProps) {
	const [activeLayer, setActiveLayer] = useState<string | null>(null);

	return (
		<div className="w-full py-12">
			<div className="relative max-w-4xl mx-auto">
				{/* Core Layers Stack */}
				<div className="space-y-4 relative z-10">
					{layers.map((layer, index) => (
						<motion.div
							key={layer.id}
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: index * 0.1 }}
							className={cn(
								"relative rounded-2xl border border-border bg-card/50 backdrop-blur-sm overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg hover:border-primary/50",
								activeLayer === layer.id
									? "ring-2 ring-primary ring-offset-2 ring-offset-background"
									: "",
							)}
							onClick={() =>
								setActiveLayer(activeLayer === layer.id ? null : layer.id)
							}
							onMouseEnter={() => setActiveLayer(layer.id)}
						>
							<div className="p-6 flex items-start gap-4">
								<div
									className={cn(
										"p-3 rounded-xl text-white shadow-md",
										layer.color,
									)}
								>
									<layer.icon className="w-6 h-6" />
								</div>
								<div className="flex-1">
									<div className="flex justify-between items-center mb-2">
										<h3 className="text-xl font-bold">
											{language === "zh" ? layer.title : layer.enTitle}
										</h3>
										{activeLayer === layer.id && (
											<motion.span
												layoutId="active-indicator"
												className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full"
											>
												{language === "zh" ? "查看详情" : "Active"}
											</motion.span>
										)}
									</div>
									<p className="text-muted-foreground mb-4 max-w-2xl">
										{language === "zh"
											? layer.description
											: layer.enDescription}
									</p>

									<AnimatePresence>
										{activeLayer === layer.id && (
											<motion.div
												initial={{ height: 0, opacity: 0 }}
												animate={{ height: "auto", opacity: 1 }}
												exit={{ height: 0, opacity: 0 }}
												className="overflow-hidden"
											>
												<div className="pt-4 border-t border-border/50 grid grid-cols-1 md:grid-cols-3 gap-3">
													{(language === "zh"
														? layer.features
														: layer.enFeatures
													).map((feature, i) => (
														<div
															key={i}
															className="flex items-center gap-2 text-sm font-medium bg-secondary/50 p-2 rounded-lg"
														>
															<div
																className={cn(
																	"w-1.5 h-1.5 rounded-full",
																	layer.color,
																)}
															/>
															{feature}
														</div>
													))}
												</div>
											</motion.div>
										)}
									</AnimatePresence>
								</div>
							</div>
						</motion.div>
					))}
				</div>

				{/* Cross-Platform Foundation */}
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ delay: 0.6 }}
					className="mt-8 relative"
				>
					<div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 blur-xl rounded-3xl" />
					<div className="relative rounded-2xl border-2 border-dashed border-primary/30 bg-card/30 p-6 flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
						<div className="p-4 bg-gradient-to-br from-primary to-accent rounded-2xl shadow-xl text-white">
							<ArrowRightLeft className="w-8 h-8" />
						</div>
						<div className="flex-1">
							<h3 className="text-lg font-bold flex items-center justify-center md:justify-start gap-2">
								<Layers className="w-4 h-4 text-primary" />
								{language === "zh"
									? "跨平台服务层：符文桥 (Rune Bridge)"
									: "Cross-Platform Service: Rune Bridge"}
							</h3>
							<p className="text-sm text-muted-foreground mt-1">
								{language === "zh"
									? "连接多个 BTC DeFi 平台，作为中立、可扩展的服务层，降低系统性摩擦。"
									: "Connecting multiple BTC DeFi platforms as a neutral, scalable service layer to reduce systemic friction."}
							</p>
						</div>
					</div>
				</motion.div>
			</div>
		</div>
	);
}
